import { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { ScrollArea } from './ui/scroll-area';
import { Separator } from './ui/separator';
import { Plus, MessageSquare, Send, Bot, User, Trash2, Edit } from 'lucide-react';
import type { Project } from './ProjectSidebar';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

interface ChatSession {
  id: string;
  name: string;
  messages: ChatMessage[];
  createdAt: Date;
}

interface ChatInterfaceProps {
  project: Project;
  onProjectUpdate: (projectId: string, updates: Partial<Project>) => void;
}

export function ChatInterface({ project, onProjectUpdate }: ChatInterfaceProps) {
  const [chatSessions, setChatSessions] = useState<ChatSession[]>([
    {
      id: '1',
      name: 'General Discussion',
      messages: [
        {
          id: '1',
          role: 'user',
          content: 'Hello! I need help with this project.',
          timestamp: new Date(Date.now() - 3600000),
        },
        {
          id: '2',
          role: 'assistant',
          content: 'Hello! I\'d be happy to help you with your project. Based on the context you\'ve provided, I can see this is about creating a chat interface. What specific aspect would you like to work on?',
          timestamp: new Date(Date.now() - 3500000),
        }
      ],
      createdAt: new Date(Date.now() - 86400000),
    }
  ]);
  
  const [activeSessionId, setActiveSessionId] = useState<string>('1');
  const [newSessionName, setNewSessionName] = useState('');
  const [showNewSession, setShowNewSession] = useState(false);
  const [messageInput, setMessageInput] = useState('');

  const activeSession = chatSessions.find(s => s.id === activeSessionId);

  const createNewSession = () => {
    if (!newSessionName.trim()) return;

    const newSession: ChatSession = {
      id: Date.now().toString(),
      name: newSessionName,
      messages: [],
      createdAt: new Date(),
    };

    setChatSessions(prev => [...prev, newSession]);
    setActiveSessionId(newSession.id);
    setNewSessionName('');
    setShowNewSession(false);

    // Update project with new session count
    onProjectUpdate(project.id, {
      chatSessions: [...project.chatSessions, {
        id: newSession.id,
        name: newSession.name,
        messageCount: 0,
      }]
    });
  };

  const sendMessage = () => {
    if (!messageInput.trim() || !activeSession) return;

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: messageInput,
      timestamp: new Date(),
    };

    // Simulate AI response
    const aiMessage: ChatMessage = {
      id: (Date.now() + 1).toString(),
      role: 'assistant',
      content: `I understand you're asking about "${messageInput}". Based on the project context and files you've provided, I can help you with this. What specific aspect would you like me to focus on?`,
      timestamp: new Date(Date.now() + 1000),
    };

    setChatSessions(prev => prev.map(session => 
      session.id === activeSessionId 
        ? { ...session, messages: [...session.messages, userMessage, aiMessage] }
        : session
    ));

    setMessageInput('');

    // Update project message count
    onProjectUpdate(project.id, {
      chatSessions: project.chatSessions.map(s => 
        s.id === activeSessionId 
          ? { ...s, messageCount: s.messageCount + 2 }
          : s
      )
    });
  };

  const deleteSession = (sessionId: string) => {
    setChatSessions(prev => prev.filter(s => s.id !== sessionId));
    
    if (activeSessionId === sessionId) {
      const remainingSessions = chatSessions.filter(s => s.id !== sessionId);
      setActiveSessionId(remainingSessions[0]?.id || '');
    }

    onProjectUpdate(project.id, {
      chatSessions: project.chatSessions.filter(s => s.id !== sessionId)
    });
  };

  return (
    <div className="flex h-full">
      {/* Chat Sessions Sidebar */}
      <div className="w-80 border-r bg-card p-4 flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <h3>Chat Sessions</h3>
          <Button
            onClick={() => setShowNewSession(true)}
            size="sm"
            variant="outline"
            className="h-8 w-8 p-0"
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>

        {showNewSession && (
          <div className="mb-4 p-3 border rounded-lg bg-muted/50">
            <Input
              placeholder="Session name"
              value={newSessionName}
              onChange={(e) => setNewSessionName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') createNewSession();
                if (e.key === 'Escape') {
                  setShowNewSession(false);
                  setNewSessionName('');
                }
              }}
              className="mb-2"
              autoFocus
            />
            <div className="flex gap-2">
              <Button onClick={createNewSession} size="sm" className="flex-1">
                Create
              </Button>
              <Button
                onClick={() => {
                  setShowNewSession(false);
                  setNewSessionName('');
                }}
                size="sm"
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        <ScrollArea className="flex-1">
          <div className="space-y-2">
            {chatSessions.map((session) => (
              <Card
                key={session.id}
                className={`p-3 cursor-pointer transition-colors hover:bg-accent/50 ${
                  activeSessionId === session.id ? 'bg-accent border-primary' : ''
                }`}
                onClick={() => setActiveSessionId(session.id)}
              >
                <div className="flex items-start justify-between mb-2">
                  <h4 className="font-medium truncate flex-1">{session.name}</h4>
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteSession(session.id);
                    }}
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 hover:text-destructive"
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <MessageSquare className="h-3 w-3" />
                  <span>{session.messages.length} messages</span>
                </div>
              </Card>
            ))}
          </div>
        </ScrollArea>
      </div>

      {/* Chat Messages Area */}
      <div className="flex-1 flex flex-col">
        {activeSession ? (
          <>
            <div className="p-4 border-b bg-card">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="font-medium">{activeSession.name}</h2>
                  <p className="text-sm text-muted-foreground">
                    {activeSession.messages.length} messages
                  </p>
                </div>
                <Badge variant="outline">
                  {project.context ? 'Context Active' : 'No Context'}
                </Badge>
              </div>
            </div>

            <ScrollArea className="flex-1 p-4">
              <div className="space-y-4 max-w-4xl mx-auto">
                {project.context && (
                  <Card className="bg-blue-50 border-blue-200 dark:bg-blue-950 dark:border-blue-800">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Project Context</CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <p className="text-sm text-muted-foreground">{project.context}</p>
                    </CardContent>
                  </Card>
                )}

                {activeSession.messages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`flex gap-3 max-w-[80%] ${
                        message.role === 'user' ? 'flex-row-reverse' : 'flex-row'
                      }`}
                    >
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                          message.role === 'user'
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted text-muted-foreground'
                        }`}
                      >
                        {message.role === 'user' ? (
                          <User className="h-4 w-4" />
                        ) : (
                          <Bot className="h-4 w-4" />
                        )}
                      </div>
                      <div
                        className={`p-3 rounded-lg ${
                          message.role === 'user'
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted'
                        }`}
                      >
                        <p className="whitespace-pre-wrap">{message.content}</p>
                        <p
                          className={`text-xs mt-1 ${
                            message.role === 'user'
                              ? 'text-primary-foreground/70'
                              : 'text-muted-foreground'
                          }`}
                        >
                          {message.timestamp.toLocaleTimeString()}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>

            <div className="p-4 border-t bg-card">
              <div className="flex gap-2 max-w-4xl mx-auto">
                <Textarea
                  placeholder="Type your message..."
                  value={messageInput}
                  onChange={(e) => setMessageInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      sendMessage();
                    }
                  }}
                  rows={1}
                  className="resize-none"
                />
                <Button
                  onClick={sendMessage}
                  disabled={!messageInput.trim()}
                  className="px-3"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-center">
            <div className="text-muted-foreground">
              <MessageSquare className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p className="mb-2">No chat session selected</p>
              <p className="text-sm">Create a new session to start chatting</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}